import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import logoPath from "@/assets/logo.png";
import geopulseLogoPath from "@/assets/geopulseml-logo.png";
import { authApi } from "@/services/auth-v2.service";

export default function AuthPage() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [registerForm, setRegisterForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
  });

  const [loginForm, setLoginForm] = useState({
    username: "",
    password: "",
  });

  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  // Состояние для системных настроек
  const [userSettings, setUserSettings] = useState({
    allowRegistration: true,
    autoActivateUsers: false,
    requireEmailVerification: false,
    defaultUserRole: "",
    defaultUserOrganization: "",
    appDisplayName: "",
  });

  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  // Проверка токена при загрузке - если есть токен, редирект на главную
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      navigate("/");
    }
  }, [navigate]);

  // Загрузка настроек пользователей при монтировании компонента
  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        // Пытаемся получить настройки из публичного API
        // Если эндпоинт недоступен, используем значения по умолчанию
        const response = await fetch('/api/v1/public/settings/user');
        if (response.ok) {
          const settings = await response.json();
          setUserSettings({
            allowRegistration: settings.allowRegistration ?? true,
            autoActivateUsers: settings.autoActivateUsers ?? false,
            requireEmailVerification: settings.requireEmailVerification ?? false,
            defaultUserRole: settings.defaultUserRole || "",
            defaultUserOrganization: settings.defaultUserOrganization || "",
            appDisplayName: settings.appDisplayName || "",
          });
        }
        // Если эндпоинт недоступен, используем значения по умолчанию (уже установлены в useState)
      } catch (error) {
        // Если эндпоинт недоступен, используем значения по умолчанию
        console.debug('Настройки пользователей недоступны, используются значения по умолчанию');
      } finally {
        setIsLoadingSettings(false);
      }
    };
    
    fetchUserSettings();
  }, []);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!loginForm.username || !loginForm.password) {
      toast({
        title: "Ошибка",
        description: "Пожалуйста, заполните все поля",
        variant: "destructive",
      });
      return;
    }
    
    setLoginLoading(true);
    try {
      const result = await authApi.login({
        username: loginForm.username,
        password: loginForm.password,
      });

      if (result.mfa_required && result.mfa_token) {
        setMfaToken(result.mfa_token);
        setMfaCode("");
        toast({
          title: "Требуется 2FA",
          description: "Введите код из приложения или резервный код",
        });
        return;
      }

      if (!result.access_token) {
        throw new Error("Сервер не вернул токен доступа");
      }

      toast({
        title: "Успешно",
        description: "Вы успешно вошли в систему",
      });
      navigate("/");
    } catch (error: any) {
      console.error("Login error:", error);
      toast({
        title: "Ошибка авторизации",
        description: error?.message || "Неверное имя пользователя или пароль",
        variant: "destructive",
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken || !mfaCode.trim()) return;
    setLoginLoading(true);
    try {
      await authApi.mfaVerify(mfaToken, mfaCode.trim());
      toast({ title: "Успешно", description: "Вы успешно вошли в систему" });
      navigate("/");
    } catch (error: any) {
      toast({
        title: "Ошибка 2FA",
        description: error?.message || "Неверный код",
        variant: "destructive",
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { username, email, password, confirmPassword, firstName, lastName } = registerForm;
    
    if (!username || !email || !password || !confirmPassword) {
      toast({
        title: "Ошибка",
        description: "Пожалуйста, заполните все обязательные поля",
        variant: "destructive",
      });
      return;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: "Ошибка",
        description: "Пароли не совпадают",
        variant: "destructive",
      });
      return;
    }
    
    setRegisterLoading(true);
    try {
      await authApi.register({
        username,
        email,
        password,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
      });
      
      toast({
        title: "Успешно",
        description: "Регистрация прошла успешно, выполняется вход..."
      });
      
      navigate("/");
    } catch (error: any) {
      console.error("Register error:", error);
      toast({
        title: "Ошибка регистрации",
        description: error?.message || "Не удалось создать учетную запись",
        variant: "destructive",
      });
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Левая колонка с формой авторизации */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6">
        <Card className="w-full mx-auto shadow-lg border-0" style={{ maxWidth: '443px', boxShadow: '0 -10px 25px -3px rgba(0, 0, 0, 0.1), 0 -4px 6px -2px rgba(0, 0, 0, 0.05), 0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
          <CardHeader>
            {/* Логотип */}
            <div className="flex justify-center mb-4">
              <div className="w-40 h-40 rounded-full border-2 border-gray-300 flex items-center justify-center bg-white shadow-lg overflow-hidden">
                <img 
                  src={logoPath} 
                  alt="Логотип GeoPulse ML" 
                  className="w-36 h-36 object-cover rounded-full"
                />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">Добро пожаловать</CardTitle>
            <CardDescription className="text-center">
              {userSettings.appDisplayName ? (
                <>
                  <span className="font-medium text-foreground">{userSettings.appDisplayName}</span>
                  <br />
                </>
              ) : null}
              Войдите в систему или создайте новую учетную запись
            </CardDescription>
          </CardHeader>
          
          <Tabs defaultValue="login" className="w-full">
            {!isLoadingSettings && (
              <TabsList className={`grid ${userSettings.allowRegistration ? 'grid-cols-2' : 'grid-cols-1'} mb-4`}>
                <TabsTrigger value="login">Вход</TabsTrigger>
                {userSettings.allowRegistration && (
                  <TabsTrigger value="register">Регистрация</TabsTrigger>
                )}
              </TabsList>
            )}
            
            {/* Форма входа */}
            <TabsContent value="login">
              {mfaToken ? (
                <form onSubmit={handleMfaSubmit}>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Введите 6-значный код из приложения или резервный код.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="mfa-code">Код 2FA</Label>
                      <Input
                        id="mfa-code"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="000000"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        disabled={loginLoading}
                        autoFocus
                      />
                    </div>
                  </CardContent>
                  <CardFooter className="flex flex-col gap-2">
                    <Button type="submit" className="w-full" disabled={loginLoading || !mfaCode.trim()}>
                      {loginLoading ? "Проверка…" : "Подтвердить"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={() => {
                        setMfaToken(null);
                        setMfaCode("");
                      }}
                    >
                      Назад
                    </Button>
                  </CardFooter>
                </form>
              ) : (
              <form onSubmit={handleLoginSubmit}>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-username">Имя пользователя</Label>
                    <Input 
                      id="login-username" 
                      placeholder="Введите имя пользователя" 
                      value={loginForm.username}
                      onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                      disabled={loginLoading}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Пароль</Label>
                    <Input 
                      id="login-password" 
                      type="password" 
                      placeholder="Введите пароль" 
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                      disabled={loginLoading}
                    />
                  </div>
                </CardContent>
                
                <CardFooter>
                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={loginLoading}
                  >
                    {loginLoading ? "Вход..." : "Войти"}
                  </Button>
                </CardFooter>
              </form>
              )}
            </TabsContent>
            
            {/* Форма регистрации */}
            {!isLoadingSettings && userSettings.allowRegistration && (
              <TabsContent value="register">
                <form onSubmit={handleRegisterSubmit}>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-username">Имя пользователя*</Label>
                      <Input 
                        id="register-username" 
                        placeholder="Введите имя пользователя" 
                        value={registerForm.username}
                        onChange={(e) => setRegisterForm({...registerForm, username: e.target.value})}
                        disabled={registerLoading}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email*</Label>
                      <Input 
                        id="register-email" 
                        type="email" 
                        placeholder="Введите email" 
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                        disabled={registerLoading}
                      />
                    </div>
                  
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Пароль*</Label>
                      <Input 
                        id="register-password" 
                        type="password" 
                        placeholder="Введите пароль" 
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                        disabled={registerLoading}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-confirm-password">Подтверждение пароля*</Label>
                      <Input 
                        id="register-confirm-password" 
                        type="password" 
                        placeholder="Повторите пароль" 
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                        disabled={registerLoading}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="register-first-name">Имя</Label>
                        <Input 
                          id="register-first-name" 
                          placeholder="Имя" 
                          value={registerForm.firstName}
                          onChange={(e) => setRegisterForm({...registerForm, firstName: e.target.value})}
                          disabled={registerLoading}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-last-name">Фамилия</Label>
                        <Input 
                          id="register-last-name" 
                          placeholder="Фамилия" 
                          value={registerForm.lastName}
                          onChange={(e) => setRegisterForm({...registerForm, lastName: e.target.value})}
                          disabled={registerLoading}
                        />
                      </div>
                    </div>
                  </CardContent>
                  
                  <CardFooter>
                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={registerLoading}
                    >
                      {registerLoading ? "Регистрация..." : "Зарегистрироваться"}
                    </Button>
                  </CardFooter>
                </form>
              </TabsContent>
            )}
          </Tabs>
        </Card>
      </div>
      
      {/* Правая колонка с информацией о приложении */}
      <div
        className="w-full md:w-1/2 text-white p-12 flex items-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/map_fon_r.avif')" }}
      >
        <div className="max-w-xl mx-auto">
          {/* Логотип GeoPulseML */}
          <div className="flex justify-center mb-8">
            <img 
              src={geopulseLogoPath} 
              alt="GeoPulseML" 
              className="h-20 object-contain"
            />
          </div>
          
          <h1 className="text-4xl font-bold mb-4">Интерактивная картографическая платформа</h1>
          <p className="text-xl mb-6">
            Мощный инструмент для управления геопространственными данными, анализа территорий и 
            совместной работы над картографическими проектами.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="mr-3 bg-white text-primary rounded-full p-1 mt-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-lg">Интерактивные карты</h3>
                <p>Создавайте и редактируйте объекты на карте, добавляйте геометрии и метаданные</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="mr-3 bg-white text-primary rounded-full p-1 mt-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-lg">Система управления доступом</h3>
                <p>Гибкая настройка ролей и прав доступа для команд и органов управления</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="mr-3 bg-white text-primary rounded-full p-1 mt-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44L4.5 19.5a2.5 2.5 0 0 1 0-3.5l2.04-2.04A2.5 2.5 0 0 1 9.5 12h5a2.5 2.5 0 0 1 2.46 3.46l-2.04 2.04a2.5 2.5 0 0 1-3.5 0"></path>
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-lg">AI-ассистент</h3>
                <p>Используйте возможности искусственного интеллекта для анализа данных и генерации контента</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}